/**
 * Tracking-consent capture + storage.
 *
 * New owners arrive from an external marketing site that has already collected a
 * cookie / tracking consent choice; it hands that choice off to the app inside the
 * base64 `startProperties` URL param (see `../startProperties.ts`). We persist it here so
 * the tracking scripts wired up later (GA via GTM, PostHog — see `./providers.ts`) can
 * gate themselves on it. Consent is a per-BROWSER decision (like a cookie banner), so it
 * lives in `localStorage`, NOT the cross-device user-preferences API.
 */
import { parseStartProperties, readRawStartProperties } from '../startProperties';

/** The three consent categories handed off from the marketing site. */
export interface TrackingConsent {
  /** Strictly-necessary scripts. Effectively always allowed; carried for completeness. */
  required: boolean;
  /** Performance / analytics tracking (GA, PostHog, …). */
  performance: boolean;
  /** Marketing / advertising tracking (retargeting pixels, ad-conversion tags, …). */
  marketing: boolean;
}

/** A stored consent record: the choice plus when it was captured (a consent audit trail). */
export interface StoredTrackingConsent extends TrackingConsent {
  /** ISO-8601 timestamp of when the consent was captured. */
  capturedAt: string;
}

/** `localStorage` key holding the captured {@link StoredTrackingConsent}. App-wide. */
export const CONSENT_STORAGE_KEY = 'ows-tracking-consent';

/**
 * Persist the owner's tracking-consent choice (browser-scoped). Silently no-ops when
 * `localStorage` is unavailable (private mode / disabled) — consent that can't be stored
 * simply isn't remembered, which fails safe: nothing downstream should track without a
 * stored opt-in.
 */
export function saveTrackingConsent(consent: TrackingConsent): void {
  const record: StoredTrackingConsent = {
    required: consent.required,
    performance: consent.performance,
    marketing: consent.marketing,
    capturedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* localStorage unavailable — consent just isn't remembered (fails safe). */
  }
}

/**
 * Read the owner's tracking consent, or `null` when none was captured.
 *
 * 1. The persisted, timestamped record written on configurator entry (`saveTrackingConsent`).
 * 2. Fallback: the raw `startProperties` hand-off (URL / boot stash), so consent is readable
 *    app-wide from the FIRST paint — before the configurator has persisted it (e.g. a tracker
 *    firing on `/login` for an as-yet-unauthenticated external arrival). The stash is written by
 *    the boot shim (`src/dev.js`) regardless of auth, so it's available immediately.
 */
export function getTrackingConsent(): StoredTrackingConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredTrackingConsent> | null;
      if (parsed && typeof parsed === 'object') {
        return {
          required: parsed.required === true,
          performance: parsed.performance === true,
          marketing: parsed.marketing === true,
          capturedAt: typeof parsed.capturedAt === 'string' ? parsed.capturedAt : '',
        };
      }
    }
  } catch {
    /* corrupt stored record — fall back to the hand-off below. */
  }
  const consent = parseStartProperties(readRawStartProperties())?.consent;
  return consent ? { ...consent, capturedAt: '' } : null;
}
